import { fromZonedTime, toZonedTime, format } from 'date-fns-tz';

export const DEFAULT_TZ = 'America/Chicago';
const CHICAGO_TZ = DEFAULT_TZ;

/**
 * Convert an IANA timezone into a friendly US label (ET/CT/MT/PT).
 * Falls back to an Intl-provided short name (e.g. "GMT-5") or the IANA string.
 *
 * Note: We intentionally normalize EST/EDT -> ET, CST/CDT -> CT, etc.
 */
export const getTimeZoneLabel = (timeZone?: string): string => {
  const tz = (timeZone || DEFAULT_TZ).trim();

  // Common explicit mappings
  if (tz === 'America/New_York') return 'ET';
  if (tz === 'America/Chicago') return 'CT';
  if (tz === 'America/Denver') return 'MT';
  if (tz === 'America/Phoenix') return 'AZ';
  if (tz === 'America/Los_Angeles') return 'PT';
  if (tz === 'America/Anchorage') return 'AK';
  if (tz === 'Pacific/Honolulu') return 'HI';

  // Best-effort fallback
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const name = (parts.find(p => p.type === 'timeZoneName')?.value || '').replace(/\u200e/g, '').trim();
    if (!name) return tz;

    // Normalize DST-specific names to the region label
    if (/E[SD]T/.test(name)) return 'ET';
    if (/C[SD]T/.test(name)) return 'CT';
    if (/M[SD]T/.test(name)) return 'MT';
    if (/P[SD]T/.test(name)) return 'PT';

    return name;
  } catch {
    return tz;
  }
};

/**
 * Convert a date to Chicago timezone
 */
export const toChicagoTime = (date: Date): Date => {
  return toZonedTime(date, CHICAGO_TZ);
};

/**
 * Convert a Chicago time to UTC for storage
 */
export const chicagoTimeToUtc = (date: Date): Date => {
  return fromZonedTime(date, CHICAGO_TZ);
};

/**
 * Format a date in Chicago time with timezone indicator
 */
export const formatChicagoTime = (date: Date, formatStr: string = 'h:mm a'): string => {
  const chicagoDate = toChicagoTime(date);
  return `${format(chicagoDate, formatStr, { timeZone: CHICAGO_TZ })} CT`;
};

/**
 * Format a date in Chicago time without timezone indicator
 */
export const formatChicagoTimeOnly = (date: Date, formatStr: string = 'h:mm a'): string => {
  const chicagoDate = toChicagoTime(date);
  return format(chicagoDate, formatStr, { timeZone: CHICAGO_TZ });
};

/**
 * Create a date in Chicago timezone from date and time inputs
 */
export const createChicagoDate = (dateStr: string, timeStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  
  // Create date in Chicago timezone
  const chicagoDate = new Date(year, month - 1, day, hour, minute);
  
  // Convert to UTC for storage
  return chicagoTimeToUtc(chicagoDate);
};

/**
 * Create a UTC Date from a local date/time string in a specific IANA timezone
 * Example: createDateInZone('2025-09-08', '15:30', 'America/Chicago')
 */
export const createDateInZone = (dateStr: string, timeStr: string, timeZone?: string): Date => {
  const tz = timeZone || DEFAULT_TZ;
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const zoned = new Date(year, month - 1, day, hour, minute);
  return fromZonedTime(zoned, tz);
};

/** Format a date in the given timezone (defaults to Chicago) */
export const formatTimeInZone = (date: Date, timeZone?: string, formatStr: string = 'h:mm a'): string => {
  const tz = timeZone || DEFAULT_TZ;
  const zoned = toZonedTime(date, tz);
  return format(zoned, formatStr, { timeZone: tz });
};

/**
 * Get current time in Chicago timezone
 */
export const getCurrentChicagoTime = (): Date => {
  return toChicagoTime(new Date());
};

/**
 * Check if current Chicago time is between start and end times
 */
export const isCurrentlyLiveInChicago = (startTime: Date, endTime: Date): boolean => {
  const now = getCurrentChicagoTime();
  const chicagoStart = toChicagoTime(startTime);
  const chicagoEnd = toChicagoTime(endTime);
  
  return chicagoStart <= now && now <= chicagoEnd;
}; 